import {
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  DualModeKey,
  fnKeyType,
  DKS,
  RS,
  SOCD,
  Macro,
  macro,
  mouseKey,
} from '../../../../types/keyboardKeys'
import { KeyboardKeysFeature } from '../../../../features/keyboard-keys/contract'
import { ADDRESS_CH585M } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'
import {
  DeepNestedLevelAddress,
  CommonAddress,
  LevelAddress,
  NestedLevelAddress,
} from '../../../../types/protocol'
import { isMacOS } from '../../../../utils/envString'
import {
  genrateMacroPackageAddressLevel,
  indexToRowCol,
  isValidRowCol,
  rowColToIndex,
} from '../../protocols/addressGenerators'
import { platform } from '../../../../types/config'
import { MemoryMacroProfile, MemoryMacroStore } from '../../../shared/feature-impls/memory-macro'

const maxPackageLen = 24 // 单包最大有效数据长度
const CH585M_MEMORY_MACRO_PROFILE: MemoryMacroProfile = {
  packageAddresses: ADDRESS_CH585M.MACRO_PACKAGE_ADDRESS,
  nameAddresses: ADDRESS_CH585M.MACRO_NAME_ADDRESS,
  maxPackageLen,
  packageReadLength: maxPackageLen + 4,
  listBatchSize: 8,
  activationDelayMs: 200,
  allowEmptyMacroPayload: true,
  generatePackageAddresses: genrateMacroPackageAddressLevel,
  isEmptyListName: (bytes) => bytes.every((value) => value === 255),
}

/**
 * @file CH585MKeys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-03-31
 * @description CH585M 键位功能实现
 */
export class CH585MKeyboardKeys extends KeyboardKeysFeature {
  private readonly macroStore = new MemoryMacroStore<Macro>({
    device: this.device,
    profile: CH585M_MEMORY_MACRO_PROFILE,
    read: sendRead,
    write: sendWrite,
  })

  private targetAdderss(isFN?: boolean, platform?: platform): DeepNestedLevelAddress {
    // 优先使用传入的 platform，否则根据当前环境判断是否是 macOS
    const isTargetMac = platform === 'mac' || (!platform && isMacOS)

    return isTargetMac
      ? isFN
        ? ADDRESS_CH585M.MAC_FN_KEY_MATRIX
        : ADDRESS_CH585M.MAC_KEY_MATRIX
      : isFN
        ? ADDRESS_CH585M.WIN_FN_KEY_MATRIX
        : ADDRESS_CH585M.WIN_KEY_MATRIX
  }
  /**
   * 获取指定层的按键总数（不读写硬件，只遍历地址表）
   * @param isFN 是否为Fn层
   * @param platform 平台标识
   * @returns 该层按键数量
   */
  getLayerKeyCount(isFN?: boolean, platform?: platform): number {
    const addressMap = this.targetAdderss(isFN, platform)[
      this.device.currentConfig.index
    ] as NestedLevelAddress
    let count = 0
    for (const row in addressMap) {
      const colMap = addressMap[row] as LevelAddress
      count += Object.keys(colMap).length
    }
    return count
  }
  /**
   * 获取当前层键位映射
   * 自动区分windows和macOS，根据系统不同读取不同地址
   * @param isFN 是否为Fn层
   */
  async getLayerKeysMap(isFN?: boolean, platform?: platform): Promise<LayerKeysMap> {
    console.log('[CH585M] Getting layer keys map')
    const ADDRESSMap = this.targetAdderss(isFN, platform)[this.device.currentConfig.index]
    const readPromises = [] as Promise<keyMapItem>[]
    const LayoutKeys = this.device.manifest.layout.keys
    for (const row in ADDRESSMap) {
      const colADDRESS = ADDRESSMap[row]
      for (const col in colADDRESS) {
        if (!isValidRowCol(parseInt(row), parseInt(col), LayoutKeys)) {
          continue
        }
        const ADDRESS = colADDRESS[col]
        const readPromise = new Promise<keyMapItem>((resolve, reject) => {
          sendRead(this.device, ADDRESS, 0x10, (res: DataView) => {
            // 解析按键属性
            const keyAttr = this.disposeRead(res)
            const index = rowColToIndex(parseInt(row), parseInt(col))
            const keyMapItem: keyMapItem = {
              index,
              keyAttr,
            }
            resolve(keyMapItem)
          }).catch(reject)
        })
        readPromises.push(readPromise)
      }
    }
    return await Promise.all(readPromises)
  }

  /**
   * 【流式】设置层键映射表
   * @param isFN 是否为Fn层
   * @param batchSize 每批读取的键位数量（默认22个）
   * @returns 异步迭代器，逐批返回 keyMapItem 数组
   */
  async *getLayerKeysMapStream(
    isFN?: boolean,
    batchSize: number = 22,
    platform?: platform,
  ): AsyncIterable<LayerKeysMap> {
    console.log('[CH585M] Streaming layer keys map')

    // 1. 获取目标地址表：类型为 NestedLevelAddress（行×列×CommonAddress）
    const addressMap = this.targetAdderss(isFN, platform)[
      this.device.currentConfig.index
    ] as NestedLevelAddress
    const keyAddressList: { row: number; col: number; address: CommonAddress }[] = []

    const LayoutKeys = this.device.manifest.layout.keys

    // 2. 遍历行/列，收集单个按键的 CommonAddress（4字节数组）
    for (const rowStr in addressMap) {
      const row = parseInt(rowStr, 10)
      const colAddressMap = addressMap[row] as LevelAddress // 某一行的所有列地址

      for (const colStr in colAddressMap) {
        const col = parseInt(colStr, 10)
        const address = colAddressMap[col] as CommonAddress // 单个按键的4字节地址

        if (!isValidRowCol(row, col, LayoutKeys)) {
          continue
        }

        keyAddressList.push({ row, col, address })
      }
    }

    // 3. 分批读取硬件数据
    const totalCount = keyAddressList.length
    for (let start = 0; start < totalCount; start += batchSize) {
      const end = Math.min(start + batchSize, totalCount)
      const batchKeys = keyAddressList.slice(start, end)

      // 4. 并发读取当前批次的键位数据
      const batchPromises = batchKeys.map(({ row, col, address }) => {
        return new Promise<keyMapItem>((resolve, reject) => {
          // sendRead 仅接收 CommonAddress（4字节数组），类型完全匹配
          sendRead(this.device, address, 0x10, (res: DataView) => {
            const keyAttr = this.disposeRead(res)
            const index = rowColToIndex(row, col)
            resolve({ index, keyAttr })
          }).catch(reject)
        })
      })

      // 5. 等待批次读取完成，流式返回
      const batchResult = await Promise.all(batchPromises)
      yield batchResult

      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  /**
   * TODO 说是其他主控也差不多一样的数据结构，但没做到以前先不抽出去，后续做到了再看是否真一致
   * 因为说能做到一样的数据原因是和固件battle后的结果，是否真遵守这里先存疑
   * 下面的发送前处理disposeSend也是一样不要急着先抽
   */
  // 键盘数据处理器
  private disposeRead(result: DataView): keyAttr {
    const data = {} as keyAttr
    // 功能类型
    data.type = result.getUint8(4)
    switch (result.getUint8(4)) {
      case 0x00:
        // 禁用键
        break
      case 0x01:
        // 键盘普通键
        const length = result.getUint8(5) // 字节5：HID键数量
        data.modifyKey = result.getUint8(6) // 字节6：Modify_key修饰键
        // 读取最多6个HID键码（字节7~12），按实际长度读取
        data.code = []
        for (let i = 0; i < Math.min(length, 6); i++) {
          data.code.push(result.getUint8(7 + i))
        }
        break
      case 0x02:
        // 键盘多媒体键 小端开始读取两位（低位+高位）
        data.code = [result.getUint16(5, true)]
        break
      case 0x03:
        // 键盘电源键
        data.code = [result.getUint8(5)]
        break
      case 0x04:
        // 鼠标功能
        const mouseBtnByte = result.getUint8(5) // 协议字节2：Mouse_key 鼠标按键位映射

        // 解析鼠标按键（bit0~bit4 对应 Left/Right/Middle/Button4/Button5）
        const mouseKey: mouseKey = [
          (mouseBtnByte >> 0) & 0x01, // 下标0: 左键 (bit0)
          (mouseBtnByte >> 1) & 0x01, // 下标1: 右键 (bit1)
          (mouseBtnByte >> 2) & 0x01, // 下标2: 中键 (bit2)
          (mouseBtnByte >> 3) & 0x01, // 下标3: 侧键4 (bit3)
          (mouseBtnByte >> 4) & 0x01, // 下标4: 侧键5 (bit4)
        ]

        // 协议字节3：X轴高4bit(bit4~7) / Y轴高4bit(bit0~3)
        const axisHighByte = result.getUint8(6)
        const xHigh4 = (axisHighByte & 0xf0) >> 4 // 提取X轴12位数据的高4位
        const yHigh4 = axisHighByte & 0x0f // 提取Y轴12位数据的高4位

        // 协议字节4：X轴低8位 | 协议字节5：Y轴低8位
        const xLow8 = result.getUint8(7)
        const yLow8 = result.getUint8(8)

        // 拼接12位完整数据
        let x = (xHigh4 << 8) | xLow8
        let y = (yHigh4 << 8) | yLow8

        // 12位有符号数符号扩展（兼容负数位移，范围-2048~2047）
        // 12位的最高位bit11为符号位，若为1则转换为JS负数
        if (x & 0x800) x -= 0x1000
        if (y & 0x800) y -= 0x1000

        // 协议字节6：垂直滚轮(int8) | 协议字节7：水平滚轮(int8)
        const verticalWheel = result.getInt8(9) // 垂直滚轮（上下）
        const horizontalWheel = result.getInt8(10) // 水平滚轮（左右）

        // 组装mouse对象到keyAttr，兼容原字段命名
        data.mouse = {
          mouseKey,
          x,
          y,
          colWheelNum: verticalWheel,
          rowWheelNum: horizontalWheel,
        }
        break
      case 0x05:
        // 鼠标键功能
        const mouseKeyByte = result.getUint8(5) // 字节5：Mouse_key 按键位映射
        // 解析鼠标按键（bit0~bit4 对应 Left/Right/Middle/Button4/Button5）
        const parsedMouseKey: mouseKey = [
          (mouseKeyByte >> 0) & 0x01, // 左键 bit0
          (mouseKeyByte >> 1) & 0x01, // 右键 bit1
          (mouseKeyByte >> 2) & 0x01, // 中键 bit2
          (mouseKeyByte >> 3) & 0x01, // Button4 bit3
          (mouseKeyByte >> 4) & 0x01, // Button5 bit4
        ]
        // 字节6：点击次数
        const clickNum = result.getUint8(6)
        // 字节7~8：press_Time uint16小端序，单位ms
        const pressTime = result.getUint16(7, true)
        // 字节9~10：relase_Time uint16小端序，单位ms
        const relaseTime = result.getUint16(9, true)

        // 组装到data
        data.mouseKey = {
          mouseKey: parsedMouseKey,
          clickNum,
          pressTime,
          relaseTime,
        }
        break
      case 0x06:
      case 0x07:
        // 鼠标滚轮功能（0x06垂直 / 0x07水平）
        const wheelType = result.getUint8(5) // 字节5：滚轮类型
        const wheelNumber = result.getInt8(6) // 字节6：每次滚动格数
        const wheelTime = result.getUint16(7, true) // 字节7~8：间隔时间（小端序）

        // 统一组装wheel对象
        const wheelData = {
          type: wheelType,
          number: wheelNumber,
          time: wheelTime,
        }

        // 根据功能码赋值给对应字段
        if (data.type === 0x06) {
          data.colWheel = wheelData
        } else {
          data.rowWheel = wheelData
        }
        break
      case 0x08:
        // 宏定义功能
        const macro: macro = {
          loop: result.getUint16(5, true),
          index: result.getUint8(7),
        }
        data.macro = macro
        break
      case 0x09:
        // 功能切换键
        data.code = [result.getUint8(5)]
        data.confIndex = result.getUint8(6)
        break
      case 0x0a:
      // 按住/单击 MT 功能
      case 0x0b:
        // 切换开关(TGL)功能
        const dualModeKey: DualModeKey = {
          // 1. 解析长按时间：字节5-6
          time: result.getUint16(5, true),
          // 2. 解析单击键：字节7=类型，字节8=键码（文档里单击键最长3字节，基础场景取字节8）
          clickType: result.getUint8(7) as fnKeyType,
          clickKeyCode: result.getUint16(8, true),
          // 3. 解析长按键：字节9=类型，字节10=键码（长按键最长3字节，基础场景取字节10）
          longPressType: result.getUint8(10) as fnKeyType,
          longPressKeyCode: result.getUint16(11, true),
        }
        data.dualModeKey = dualModeKey
        break
      case 0x0c:
        // 动态键程(DKS)功能
        const dks: DKS = {
          actPoint1: 0,
          actPoint2: 0,
          releasePoint1: 0,
          releasePoint2: 0,
          triggerBits: [],
          keyCodes: [],
        }

        // 读取6字节行程原始数据 (字节5~10)
        const B1 = result.getUint8(5) // 第1字节
        const B2 = result.getUint8(6) // 第2字节
        const B3 = result.getUint8(7) // 第3字节
        const B4 = result.getUint8(8) // 第4字节
        const B5 = result.getUint8(9) // 第5字节
        const B6 = result.getUint8(10) // 第6字节

        // 解析4个12bit行程
        // 按下位置1：低8位(B1) + 高4位(B2低4位左移8位)
        dks.actPoint1 = B1 + ((B2 & 0x0f) << 8)
        // 按下位置2：低4位(B2高4位右移4位) + 高8位(B3左移4位)
        dks.actPoint2 = ((B2 & 0xf0) >> 4) + (B3 << 4)
        // 抬起位置1：低8位(B4) + 高4位(B5低4位左移8位)
        dks.releasePoint1 = B4 + ((B5 & 0x0f) << 8)
        // 抬起位置2：低4位(B5高4位右移4位) + 高8位(B6左移4位)
        dks.releasePoint2 = ((B5 & 0xf0) >> 4) + (B6 << 4)

        // 读取4个触发位 (字节11~14)
        /**
         * 0b00 没有按键事件键码 x 维持最近状态
         * 0b01 按键 x 按下事件
         * 0b10 按键 x 释放事件
         * 0b11 按键先按下后释放
         * ——————————————————————————————————————————————
         * |00         |00        |00        |00        |
         * ——————————————————————————————————————————————
         * |抬起0.2mm  |抬起3.3mm |按下3.3mm |按下0.2mm |
         * ——————————————————————————————————————————————
         * 例 03 24 24 c0
         * 03： 0000 0011 按键1在[按下0.2mm]处按下释放
         * 24： 0010 0100 按键2在[按下3.3mm]处按下，[抬起3.3mm]处抬起
         * 24： 0010 0100 按键3在[按下3.3mm]处按下，[抬起3.3mm]处抬起
         * c0： 1100 0000 按键4在[抬起0.2mm]处按下释放
         */
        dks.triggerBits = [
          result.getUint8(11), // 键码1触发位
          result.getUint8(12), // 键码2触发位
          result.getUint8(13), // 键码3触发位
          result.getUint8(14), // 键码4触发位
        ]

        // 读取4个HID键码 (字节15~18)
        dks.keyCodes = [
          result.getUint8(15), // 键码1 HID
          result.getUint8(16), // 键码2 HID
          result.getUint8(17), // 键码3 HID
          result.getUint8(18), // 键码4 HID
        ]

        data.dks = dks
        break
      case 0x0d:
        // RS 功能
        const rs = {} as RS
        rs.combineRT = result.getUint8(5) // 字节5：结合RT (0=不结合/1=结合)
        rs.relatedKeyRow = result.getUint8(6) // 字节6：关联键行索引
        rs.relatedKeyCol = result.getUint8(7) // 字节7：关联键列索引
        rs.relatedKeyIndex = rowColToIndex(rs.relatedKeyRow, rs.relatedKeyCol)
        rs.triggerKeyType = result.getUint8(8) as fnKeyType // 字节8：触发键类型
        rs.triggerKeyCode = result.getUint8(9) // 字节9：触发键码（修饰键/HID码）
        rs.triggerKeyExtCode = result.getUint8(10) // 字节10：触发键扩展码
        rs.triggerThreshold = result.getUint16(11, true) // 字节11：RS触发阈值（0.01mm）
        data.rs = rs
        break
      case 0x0e:
        // SOCD
        const socd = {} as SOCD
        socd.subMode = result.getUint8(5) // 字节5：SOCD子模式
        socd.combineRT = result.getUint8(6) // 字节6：结合RT (0=不结合/1=结合)
        socd.triggerWhenBottom = result.getUint8(7) // 字节7：按到底都触发
        socd.relatedKeyRow = result.getUint8(8) // 字节8：关联键行索引
        socd.relatedKeyCol = result.getUint8(9) // 字节9：关联键列索引
        socd.relatedKeyIndex = rowColToIndex(socd.relatedKeyRow, socd.relatedKeyCol)
        socd.triggerKeyType = result.getUint8(10) as fnKeyType // 字节10：触发键类型（功能码）
        socd.triggerKeyCode = result.getUint8(11) // 字节11：触发键码（键码1）
        socd.triggerKeyExtCode = result.getUint8(12) // 字节12：触发键扩展码（键码2）
        socd.selfPriority = result.getUint8(13) // 字节13：本键绝对优先
        // 触发阈值：字节14~15（小端序uint16，单位0.01mm）
        socd.triggerThreshold = result.getUint16(14, true)
        data.socd = socd
        break
      case 0x0f:
        // TODO 鼠标和键盘组合键功能（仅限旋钮定义更改）
        break
    }
    return data
  }

  /**
   * 设置当前层键位映射
   * 自动区分windows和macOS，根据系统不同读取不同地址
   * @param keysMap 键位映射
   * @param isFN 是否为Fn层
   */
  async setLayerKeysMap(keysMap: keyMapItem, isFN?: boolean, platform?: platform): Promise<void> {
    console.log('[CH585M] Setting layer keys map')
    // 将index转化为6*22行列排序的实际坐标
    const { index } = keysMap
    const { row, col } = indexToRowCol(index)
    const ADDRESS = getAddress(
      this.targetAdderss(isFN, platform),
      this.device.currentConfig.index,
      row,
      col,
    )
    const data = this.disposeSend(keysMap.keyAttr)
    return sendWrite(this.device, ADDRESS, Array.from(data))
  }

  // 发送前处理
  private disposeSend(data: keyAttr): Uint8Array {
    const buffer = new ArrayBuffer(16)
    const dv = new DataView(buffer)

    // 字节0：功能类型
    const type = data.type ?? 0x00
    dv.setUint8(0, type)

    switch (type) {
      case 0x00:
        // 禁用键
        break
      case 0x01:
        // 键盘普通键
        if (data.code?.length) {
          const length = Math.min(data.code.length, 6) // 最多支持6个HID键
          // 字节5：长度（HID键数量）
          dv.setUint8(1, length)
          // 字节6：Modify_key修饰键（默认0，无修饰）
          dv.setUint8(2, data.modifyKey ?? 0x00)
          // 字节7~12：写入HID键码，超出6个的部分截断
          for (let i = 0; i < length; i++) {
            dv.setUint8(3 + i, data.code[i])
          }
        }
        break
      case 0x02:
        // 键盘多媒体键
        if (data.code?.length) {
          dv.setUint16(1, data.code[0], true)
        }
        break
      case 0x03:
        // 键盘电源键
        if (data.code?.length) {
          dv.setUint8(1, data.code[0])
        }
        break
      case 0x04:
        // 鼠标功能
        const mouse = data.mouse
        if (mouse) {
          // 1. 协议字节2 (result索引1)：鼠标按键位映射
          let mouseBtnByte = 0x00
          const mouseKey = mouse.mouseKey ?? [0, 0, 0, 0, 0]
          if (mouseKey[0]) mouseBtnByte |= 0x01 // 左键 (bit0)
          if (mouseKey[1]) mouseBtnByte |= 0x02 // 右键 (bit1)
          if (mouseKey[2]) mouseBtnByte |= 0x04 // 中键 (bit2)
          if (mouseKey[3]) mouseBtnByte |= 0x08 // 侧键4 (bit3)
          if (mouseKey[4]) mouseBtnByte |= 0x10 // 侧键5 (bit4)
          dv.setUint8(1, mouseBtnByte)

          // 2. 处理 X/Y 轴数据 (限制在12位有符号数范围内: -2048 ~ 2047)
          let x = Math.max(Math.min(mouse.x ?? 0, 2047), -2048)
          let y = Math.max(Math.min(mouse.y ?? 0, 2047), -2048)

          // 反向符号扩展：将JS负数转换为12位有符号数的二进制格式
          if (x < 0) x += 0x1000
          if (y < 0) y += 0x1000

          // 3. 拆分 X/Y 轴的高4位和低8位
          const xHigh4 = (x >> 8) & 0x0f // X轴高4位 (bit4~7 of 协议字节3)
          const xLow8 = x & 0xff // X轴低8位 (协议字节4)
          const yHigh4 = (y >> 8) & 0x0f // Y轴高4位 (bit0~3 of 协议字节3)
          const yLow8 = y & 0xff // Y轴低8位 (协议字节5)

          // 4. 协议字节3 (result索引2)：X高4位 + Y高4位
          const axisHighByte = (xHigh4 << 4) | yHigh4
          dv.setUint8(2, axisHighByte)

          // 5. 协议字节4 & 5 (result索引3 & 4)：X低8位 & Y低8位
          dv.setUint8(3, xLow8)
          dv.setUint8(4, yLow8)

          // 6. 协议字节6 (result索引5)：垂直滚轮 (int8)
          const colWheel = Math.max(Math.min(mouse.colWheelNum ?? 0, 127), -128)
          dv.setInt8(5, colWheel)

          // 7. 协议字节7 (result索引6)：水平滚轮 (int8)
          const rowWheel = Math.max(Math.min(mouse.rowWheelNum ?? 0, 127), -128)
          dv.setInt8(6, rowWheel)
        }
        break
      case 0x05:
        // 鼠标键功能
        const mouseKeyObj = data.mouseKey
        if (mouseKeyObj) {
          // 1. 字节1：Mouse_key 按键位映射
          let mouseBtnByte = 0x00
          const mouseKey = mouseKeyObj.mouseKey ?? [0, 0, 0, 0, 0]
          if (mouseKey[0]) mouseBtnByte |= 0x01 // 左键 bit0
          if (mouseKey[1]) mouseBtnByte |= 0x02 // 右键 bit1
          if (mouseKey[2]) mouseBtnByte |= 0x04 // 中键 bit2
          if (mouseKey[3]) mouseBtnByte |= 0x08 // Button4 bit3
          if (mouseKey[4]) mouseBtnByte |= 0x10 // Button5 bit4
          dv.setUint8(1, mouseBtnByte)

          // 2. 字节2：点击次数（限制0-255）
          const clickNum = Math.max(Math.min(mouseKeyObj.clickNum ?? 0, 255), 0)
          dv.setUint8(2, clickNum)

          // 3. 字节3~4：press_Time uint16小端序（限制0-65535）
          const pressTime = Math.max(Math.min(mouseKeyObj.pressTime ?? 0, 65535), 0)
          dv.setUint16(3, pressTime, true)

          // 4. 字节5~6：relase_Time uint16小端序（限制0-65535）
          const relaseTime = Math.max(Math.min(mouseKeyObj.relaseTime ?? 0, 65535), 0)
          dv.setUint16(5, relaseTime, true)
        }
        break
      case 0x06:
      case 0x07:
        // 鼠标滚轮功能（0x06垂直 / 0x07水平）
        // 根据功能码取对应的数据对象
        const wheelObj = data.type === 0x06 ? data.colWheel : data.rowWheel
        if (wheelObj) {
          // 1. 字节1：滚轮类型（限制0-2）
          const type = Math.max(Math.min(wheelObj.type ?? 0, 2), 0)
          dv.setUint8(1, type)

          // 2. 字节2：每次滚动格数（int8）
          const num = Math.max(Math.min(wheelObj.number ?? 0, 127), -128)
          dv.setInt8(2, num)

          // 3. 字节3~4：滚动间隔时间（uint16 小端序）
          const time = Math.max(Math.min(wheelObj.time ?? 0, 65535), 0)
          dv.setUint16(3, time, true)
        }
        break
      case 0x08:
        // 宏定义功能
        if (data?.macro) {
          dv.setUint16(1, data.macro.loop ?? 0x00, true)
          dv.setUint8(3, data.macro.index ?? 0x00)
        }
        break
      case 0x09:
        // 功能切换键
        if (data.code?.length) {
          dv.setUint8(1, data.code[0])
        }
        dv.setUint8(2, data.confIndex ?? 0x00)
        break
      case 0x0a:
      // 按住/单击 MT 功能
      case 0x0b:
        // 切换开关(TGL)功能
        const dualModeKey: DualModeKey = data.dualModeKey as DualModeKey
        if (dualModeKey) {
          // 1. 解析长按时间：字节1-2
          dv.setUint16(1, dualModeKey.time, true)
          // 2. 解析单击键：字节3=类型，字节4-5=键码
          dv.setUint8(3, dualModeKey.clickType)
          dv.setUint16(4, dualModeKey.clickKeyCode, true)
          // 3. 解析长按键：字节6=类型，字节7-8=键码
          dv.setUint8(6, dualModeKey.longPressType)
          dv.setUint16(7, dualModeKey.longPressKeyCode, true)
        }
        break
      case 0x0c:
        // 动态键程(DKS)功能
        const dks: DKS = data.dks as DKS
        if (dks) {
          // 读取6字节行程原始数据 (字节1~6)
          // 按下位置1 (12bit) -> B1(低8位) + B2低4位(高4位)
          const B1 = dks.actPoint1 & 0xff // 低8位
          const B2_low = (dks.actPoint1 >> 8) & 0x0f // 高4位

          // 按下位置2 (12bit) -> B2高4位(低4位) + B3(高8位)
          const B2_high = (dks.actPoint2 & 0x0f) << 4 // 低4位
          const B3 = (dks.actPoint2 >> 4) & 0xff // 高8位

          // 抬起位置1 (12bit) -> B4(低8位) + B5低4位(高4位)
          const B4 = dks.releasePoint1 & 0xff // 低8位
          const B5_low = (dks.releasePoint1 >> 8) & 0x0f // 高4位

          // 抬起位置2 (12bit) -> B5高4位(低4位) + B6(高8位)
          const B5_high = (dks.releasePoint2 & 0x0f) << 4 // 低4位
          const B6 = (dks.releasePoint2 >> 4) & 0xff // 高8位

          // 组合中间字节
          const B2 = B2_high | B2_low
          const B5 = B5_high | B5_low

          dv.setUint8(1, B1)
          dv.setUint8(2, B2)
          dv.setUint8(3, B3)
          dv.setUint8(4, B4)
          dv.setUint8(5, B5)
          dv.setUint8(6, B6)

          // 读取4个触发位 (字节7~10)
          if (dks.triggerBits && dks.triggerBits.length >= 4) {
            dv.setUint8(7, dks.triggerBits[0])
            dv.setUint8(8, dks.triggerBits[1])
            dv.setUint8(9, dks.triggerBits[2])
            dv.setUint8(10, dks.triggerBits[3])
          }

          // 读取4个HID键码 (字节11~14)
          if (dks.keyCodes && dks.keyCodes.length >= 4) {
            dv.setUint8(11, dks.keyCodes[0])
            dv.setUint8(12, dks.keyCodes[1])
            dv.setUint8(13, dks.keyCodes[2])
            dv.setUint8(14, dks.keyCodes[3])
          }
        }
        break
      case 0x0d:
        // RS 功能
        const rs: RS = data.rs as RS
        if (rs) {
          rs.combineRT = rs.combineRT ?? 0
          rs.relatedKeyIndex = rs.relatedKeyIndex ?? 0
          rs.triggerKeyType = rs.triggerKeyType ?? 0
          rs.triggerKeyCode = rs.triggerKeyCode ?? 0
          rs.triggerKeyExtCode = rs.triggerKeyExtCode ?? 0
          rs.triggerThreshold = rs.triggerThreshold ?? 0

          const { row: rsRow, col: rsCol } = indexToRowCol(rs.relatedKeyIndex)
          rs.relatedKeyRow = rsRow
          rs.relatedKeyCol = rsCol

          // 字节1：结合RT (0=不结合/1=结合)
          dv.setUint8(1, rs.combineRT)
          // 字节2：关联键行索引
          dv.setUint8(2, rsRow)
          // 字节3：关联键列索引
          dv.setUint8(3, rsCol)
          // 字节4：触发键类型
          dv.setUint8(4, rs.triggerKeyType)
          // 字节5：触发键码（修饰键/HID码）
          dv.setUint8(5, rs.triggerKeyCode)
          // 字节6：触发键扩展码
          dv.setUint8(6, rs.triggerKeyExtCode)
          // 字节7-8：RS触发阈值（0.01mm）
          dv.setUint16(7, rs.triggerThreshold, true)
        }
        break
      case 0x0e:
        // SOCD
        const socd: SOCD = data.socd as SOCD
        if (socd) {
          socd.subMode = socd.subMode ?? 0
          socd.combineRT = socd.combineRT ?? 0
          socd.triggerWhenBottom = socd.triggerWhenBottom ?? 0
          socd.relatedKeyIndex = socd.relatedKeyIndex ?? 0
          socd.triggerKeyType = socd.triggerKeyType ?? 0
          socd.triggerKeyCode = socd.triggerKeyCode ?? 0
          socd.triggerKeyExtCode = socd.triggerKeyExtCode ?? 0
          socd.selfPriority = socd.selfPriority ?? 0
          socd.triggerThreshold = socd.triggerThreshold ?? 0

          const { row: socdRow, col: socdCol } = indexToRowCol(socd.relatedKeyIndex)
          socd.relatedKeyRow = socdRow
          socd.relatedKeyCol = socdCol

          // SOCD 专属基础字段（字节1~3）
          dv.setUint8(1, socd.subMode) // 字节1：SOCD子模式
          dv.setUint8(2, socd.combineRT) // 字节2：结合RT (0=不结合/1=结合)
          dv.setUint8(3, socd.triggerWhenBottom) // 字节3：按到底都触发
          // 关联键索引（字节4~5）
          dv.setUint8(4, socdRow) // 字节4：关联键行索引
          dv.setUint8(5, socdCol) // 字节5：关联键列索引
          // 统一3字节触发键码（字节6~8，和RS完全一致）
          dv.setUint8(6, socd.triggerKeyType) // 字节6：触发键类型（功能码）
          dv.setUint8(7, socd.triggerKeyCode) // 字节7：触发键码（键码1）
          dv.setUint8(8, socd.triggerKeyExtCode) // 字节8：触发键扩展码（键码2）
          // SOCD 专属字段（字节9~11）
          dv.setUint8(9, socd.selfPriority) // 字节9：本键绝对优先
          // 触发阈值：字节10~11（小端序uint16，单位0.01mm，和RS完全一致）
          dv.setUint16(10, socd.triggerThreshold, true)
        }
        break
      case 0x0f:
        // TODO 鼠标和键盘组合键功能（仅限旋钮定义更改）
        break
    }

    // 转换为Uint8Array返回
    return new Uint8Array(buffer)
  }

  // 获取宏列表
  async getMacroList(): Promise<Macro[]> {
    return this.macroStore.getMacroList()
  }

  /**
   * 【流式】获取宏列表
   * @param batchSize 每批读取的宏数量
   * @returns 异步迭代器，逐批返回 Macro 数组
   */
  async *getMacroListStream(batchSize: number = 8): AsyncIterable<Macro[]> {
    console.log('[CH585M] get macro')
    for await (const macros of this.macroStore.getMacroListStream(batchSize)) {
      yield macros
    }
  }

  // 获取指定组的宏
  async getMacroByIndex(index: number): Promise<Macro> {
    return this.macroStore.getMacroByIndex(index)
  }

  // 设置指定组的宏
  async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    return this.macroStore.setMacroByIndex(macro, index)
  }
}
